/**
 * What a personal best under a mode key *is*: which keys exist, which way is
 * better, how the number reads, and which numbers a run can produce at all.
 *
 * The browser has always known these things one run at a time — a mode's
 * `score` hook answers them for the run that just ended. The server needs the
 * same answers for a number that arrives over the wire with no run attached
 * (`POST /api/account/bests`), and until `docs/decisions/0052` it had none of
 * them: it stored any key, any finite number and whatever display string the
 * client sent, and showed that string to every visitor. This file is the one
 * place both sides now read, so the two cannot disagree about what
 * `sprint:normal:lines-40` means or how 41250 is written.
 *
 * Everything here is a pure function of a `ModeConfig` and the engine's own
 * constants. Nothing here is a policy about what a *plausible* result is —
 * see `bestResultRule` for exactly what the bounds are and are not.
 */
import { DEFAULT_WIDTH, TICKS_PER_SECOND, msToTicks } from '@interlock/engine';

import {
  HARDNESS_IDS,
  SPRINT_LINE_GOALS,
  SPRINT_TIME_GOALS_MIN,
  encodeModeConfig,
} from './config.js';
import type { ModeConfig } from './config.js';
import { formatClock } from './format.js';

/** Every piece the engine deals is four cells. */
const CELLS_PER_PIECE = 4;

/**
 * The fewest ticks one placed piece is counted as taking.
 *
 * One, which is the simulation's own resolution: a tick is the smallest unit
 * of time a run is measured in (`runtime.elapsedMs` is a sum of whole ticks).
 * It is stated rather than hidden that the engine itself would execute more
 * than one hard drop inside a single tick if it were handed them — `step`
 * applies every event it is given — so this is "sixty pieces a second,
 * sustained for a whole run", not a law of the engine. It is the loosest rate
 * that can be written down from the engine's constants at all, and it is
 * several times faster than anybody plays. Anything tighter is a plausibility
 * policy, and that is the owner's to set (`docs/decisions/0052`).
 */
export const BEST_MIN_TICKS_PER_PIECE = 1;

/**
 * Every configuration a solo run can be started with, in a stable order.
 *
 * Sprint is four hardness levels by eight goals (four line targets, four time
 * limits); survival is four hardness levels and has no goal. Thirty-six in
 * all. A mode that is registered and not listed here has no keys, and
 * `test/best.test.ts` fails until it is.
 */
export function allModeConfigs(): readonly ModeConfig[] {
  const configs: ModeConfig[] = [];
  for (const hardness of HARDNESS_IDS) {
    for (const lines of SPRINT_LINE_GOALS) {
      configs.push({ modeId: 'sprint', hardness, goal: { kind: 'lines', lines } });
    }
    for (const minutes of SPRINT_TIME_GOALS_MIN) {
      configs.push({ modeId: 'sprint', hardness, goal: { kind: 'time', ms: minutes * 60_000 } });
    }
  }
  for (const hardness of HARDNESS_IDS) {
    configs.push({ modeId: 'survival', hardness });
  }
  return configs;
}

const CONFIG_BY_KEY: ReadonlyMap<string, ModeConfig> = new Map(
  allModeConfigs().map((config) => [encodeModeConfig(config), config] as const),
);

/** Every key `encodeModeConfig` can produce for a configuration that exists. */
export const MODE_KEYS: readonly string[] = [...CONFIG_BY_KEY.keys()];

/**
 * The configuration a key names, or `null`.
 *
 * The inverse of `encodeModeConfig`, and exact: a key is accepted only if it
 * is byte for byte what `encodeModeConfig` writes for a configuration in
 * `allModeConfigs`. `sprint:normal:lines-41` names a goal nobody can pick,
 * `sprint:normal:lines-040` is not how forty is written, and `survival:hard:
 * lines-40` gives a goal to a mode that has none — all three are `null`.
 */
export function decodeModeConfig(key: string): ModeConfig | null {
  return CONFIG_BY_KEY.get(key) ?? null;
}

export interface BestResultRule {
  /** What the number counts. */
  readonly unit: 'ms' | 'lines';
  readonly betterWhen: 'lower' | 'higher';
  /** True when the number is a count and a fraction of one is not a result. */
  readonly integer: boolean;
  /** The smallest value a run can produce. */
  readonly min: number;
  /** The largest, or `null` when nothing in the rules bounds it. */
  readonly max: number | null;
}

/** Pieces needed to fill `lines` rows of the well: ten cells a row, four a piece. */
export function minPiecesForLines(lines: number, width: number = DEFAULT_WIDTH): number {
  return Math.ceil((lines * width) / CELLS_PER_PIECE);
}

/** Rows `pieces` pieces can fill at most. */
export function maxLinesForPieces(pieces: number, width: number = DEFAULT_WIDTH): number {
  return Math.floor((pieces * CELLS_PER_PIECE) / width);
}

/**
 * The shape of a best under `config`, and the bounds a run cannot be outside.
 *
 * - **Sprint to a line target** is a time, lower is better. Clearing `n` rows
 *   takes `n * 10` cells, which is at least `ceil(n * 10 / 4)` pieces, at
 *   `BEST_MIN_TICKS_PER_PIECE` each: forty lines is a hundred pieces is a
 *   hundred ticks, 1.666 s. There is no upper bound; a run can take all day.
 * - **Sprint against the clock** is a line count, higher is better. A count
 *   is a non-negative integer, and the clock's ticks allow at most one piece
 *   each: three minutes is 10,800 pieces is 4,320 lines.
 * - **Survival** is a time, higher is better. A run that ended has run for at
 *   least one tick. Nothing bounds it above: the floor rises on a fixed
 *   cadence and a player who keeps clearing never tops out.
 *
 * The time bounds are floored to a whole millisecond, because `elapsedMs` is
 * a sum of 16.67 ms ticks and its last bit is not something to refuse a
 * result over.
 */
export function bestResultRule(config: ModeConfig): BestResultRule {
  switch (config.modeId) {
    case 'sprint': {
      const goal = config.goal;
      if (goal === undefined) throw new Error('a sprint configuration has a goal');
      if (goal.kind === 'lines') {
        const ticks = minPiecesForLines(goal.lines) * BEST_MIN_TICKS_PER_PIECE;
        return {
          unit: 'ms',
          betterWhen: 'lower',
          integer: false,
          min: Math.floor((ticks * 1000) / TICKS_PER_SECOND),
          max: null,
        };
      }
      const pieces = Math.floor(msToTicks(goal.ms) / BEST_MIN_TICKS_PER_PIECE);
      return {
        unit: 'lines',
        betterWhen: 'higher',
        integer: true,
        min: 0,
        max: maxLinesForPieces(pieces),
      };
    }
    case 'survival':
      return {
        unit: 'ms',
        betterWhen: 'higher',
        integer: false,
        min: Math.floor(1000 / TICKS_PER_SECOND),
        max: null,
      };
    default:
      throw new Error(`unknown mode id: ${config.modeId}`);
  }
}

/** Whether a run under `config` could have produced `value` at all. */
export function isPossibleBestResult(config: ModeConfig, value: number): boolean {
  if (!Number.isFinite(value)) return false;
  const rule = bestResultRule(config);
  if (rule.integer && !Number.isInteger(value)) return false;
  if (value < rule.min) return false;
  return rule.max === null || value <= rule.max;
}

/**
 * How a best under `config` is written.
 *
 * The same strings the two modes' own `score` hooks produce — `formatClock`
 * for a time, `"17 lines"` for a count — and `test/best.test.ts` holds the
 * two together for every configuration, so a change to either is a failing
 * test rather than a personal best that reads one way on the device that set
 * it and another on the leaderboard.
 */
export function formatBestResult(config: ModeConfig, value: number): string {
  return bestResultRule(config).unit === 'ms' ? formatClock(value) : `${String(value)} lines`;
}
